import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from '../../../i18n';

const SpinWheelElement = ({
  element,
  isEditor = false,
  canInteract = true,
  onResult,
  onUpdateContent,
  syncedRotation = null
}) => {
  const { t } = useTranslation();
  const { content } = element;
  const { segments, spinDuration = 3000 } = content;

  const [rotation, setRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const wheelRef = useRef(null);
  const spinStartTime = useRef(null);

  // Sync rotation from external source (classroom sync)
  useEffect(() => {
    if (syncedRotation !== null && !isSpinning) {
      setRotation(syncedRotation);
    }
  }, [syncedRotation, isSpinning]);

  const handleSpin = useCallback(() => {
    if (isSpinning || isEditor || !canInteract) return;

    setIsSpinning(true);
    spinStartTime.current = Date.now();

    // Random rotation: 5-10 full turns + random segment
    const extraRotations = (5 + Math.random() * 5) * 360;
    const segmentAngle = 360 / segments.length;
    const randomSegmentOffset = Math.random() * 360;
    const finalRotation = rotation + extraRotations + randomSegmentOffset;

    setRotation(finalRotation);

    // Calculate which segment will be selected
    setTimeout(() => {
      setIsSpinning(false);
      const normalizedRotation = finalRotation % 360;
      // Pointer is at top (0 degrees), wheel rotates clockwise
      // Adjust for segment calculation
      const adjustedRotation = (360 - normalizedRotation + 90) % 360;
      const selectedIndex = Math.floor(adjustedRotation / segmentAngle) % segments.length;
      const selectedSegment = segments[selectedIndex];

      if (onResult) {
        onResult({
          segment: selectedSegment,
          index: selectedIndex,
          rotation: finalRotation
        });
      }

      if (onUpdateContent) {
        onUpdateContent({ ...content, lastResult: selectedSegment.text });
      }
    }, spinDuration);
  }, [isSpinning, isEditor, canInteract, segments, rotation, spinDuration, content, onResult, onUpdateContent]);

  const segmentAngle = 360 / segments.length;

  // Generate SVG path for a wheel segment
  const getSegmentPath = (index, total, radius) => {
    const startAngle = (index * 360 / total - 90) * (Math.PI / 180);
    const endAngle = ((index + 1) * 360 / total - 90) * (Math.PI / 180);

    const x1 = radius + radius * Math.cos(startAngle);
    const y1 = radius + radius * Math.sin(startAngle);
    const x2 = radius + radius * Math.cos(endAngle);
    const y2 = radius + radius * Math.sin(endAngle);

    const largeArc = segmentAngle > 180 ? 1 : 0;

    return `M ${radius} ${radius} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  };

  // Calculate text position for segment
  const getTextPosition = (index, total, radius) => {
    const angle = ((index + 0.5) * 360 / total - 90) * (Math.PI / 180);
    const textRadius = radius * 0.65;
    return {
      x: radius + textRadius * Math.cos(angle),
      y: radius + textRadius * Math.sin(angle),
      rotation: (index + 0.5) * 360 / total
    };
  };

  const radius = 50; // SVG viewBox radius

  return (
    <div
      className="w-full h-full flex items-center justify-center relative"
      onClick={handleSpin}
      style={{ cursor: canInteract && !isEditor ? 'pointer' : 'default' }}
    >
      {/* Wheel */}
      <svg
        ref={wheelRef}
        viewBox={`0 0 ${radius * 2} ${radius * 2}`}
        className="w-full h-full"
        style={{
          transform: `rotate(${rotation}deg)`,
          transition: isSpinning ? `transform ${spinDuration}ms cubic-bezier(0.17, 0.67, 0.12, 0.99)` : 'none'
        }}
      >
        {/* Segments */}
        {segments.map((segment, index) => {
          const textPos = getTextPosition(index, segments.length, radius);
          return (
            <g key={index}>
              <path
                d={getSegmentPath(index, segments.length, radius)}
                fill={segment.color}
                stroke="#ffffff"
                strokeWidth="1"
              />
              <text
                x={textPos.x}
                y={textPos.y}
                fill="#ffffff"
                fontSize={segments.length > 8 ? "4" : "5"}
                fontWeight="bold"
                textAnchor="middle"
                dominantBaseline="middle"
                transform={`rotate(${textPos.rotation}, ${textPos.x}, ${textPos.y})`}
                style={{
                  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                  pointerEvents: 'none'
                }}
              >
                {segment.text.length > 10 ? segment.text.slice(0, 10) + '...' : segment.text}
              </text>
            </g>
          );
        })}

        {/* Center circle */}
        <circle
          cx={radius}
          cy={radius}
          r={radius * 0.12}
          fill="#ffffff"
          stroke="#e5e7eb"
          strokeWidth="2"
        />
      </svg>

      {/* Pointer (fixed, doesn't rotate) */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1"
        style={{ zIndex: 10 }}
      >
        <div
          className="w-0 h-0"
          style={{
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderTop: '16px solid #ef4444',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
          }}
        />
      </div>

      {/* Spin hint in editor */}
      {isEditor && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="bg-black/50 text-white text-xs px-2 py-1 rounded">
            {t('games.spinWheel.label')}
          </div>
        </div>
      )}
    </div>
  );
};

export default SpinWheelElement;
